import axios, { AxiosResponse } from 'axios';

// API base URL - robust construction for both localhost and Replit
export const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  if (window.location.hostname === 'localhost') {
    return 'http://localhost:8000';
  }
  
  // For production deployment (replit.app domain) and Replit workspace,
  // use the same origin since FastAPI serves both the React build and API endpoints
  if (window.location.hostname.includes('.replit.app') || window.location.hostname.includes('.replit.dev')) {
    return window.location.origin;
  }
  
  // Fallback for other environments
  return window.location.origin;
};

const API_BASE_URL = getApiBaseUrl();

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add JWT token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token expiration and errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Enhanced error handling
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    
    // Log errors for debugging
    console.error('API Error:', {
      status: error.response?.status,
      data: error.response?.data,
      url: error.config?.url,
      method: error.config?.method
    });
    
    // Return a more user-friendly error
    const enhancedError = {
      ...error,
      message: error.response?.data?.detail || error.message || 'An unexpected error occurred'
    };
    
    return Promise.reject(enhancedError);
  }
);

// Types for API responses
// Backend user response from API
interface BackendUser {
  id: number;
  uuid: string;
  email: string;
  phone: string;
  first_name: string;
  last_name: string;
  role_name: string;
  approval_status: string;
  rejection_reason?: string;
  is_super_admin?: boolean;
  is_approved?: boolean;
  is_verified?: boolean;
  email_verified?: boolean;
  phone_verified?: boolean;
  verification_status?: 'unverified' | 'email_verified' | 'phone_verified' | 'fully_verified';
  profile_complete?: boolean;
  current_latitude?: number;
  current_longitude?: number;
  rider_photo_url?: string;
  is_active: boolean;
  created_at: string;
  average_rating?: number;
  total_ratings?: number;
  account_status?: 'active' | 'suspended' | 'banned';
  suspended_at?: string;
  suspension_expires_at?: string;
  suspended_by_admin_id?: number;
  suspension_reason?: string;
  reinstated_at?: string;
  reinstated_by_admin_id?: number;
  customer_type?: 'individual' | 'corporate';
  corporate_info?: {
    company_name?: string;
    company_registration_number?: string;
    company_address?: string;
    company_registration_doc_url?: string;
    company_logo_url?: string;
  };
  merchant_info?: {
    merchant_code?: string;
    vehicle_types?: string[];
    earnings_split_percentage?: number;
    business_name?: string;
    dispatchers_count?: number;
  };
  vehicle?: {
    vehicle_type: string;
    license_plate: string;
    make?: string;
    model?: string;
    year?: number;
    color?: string;
    capacity_kg: number;
    vehicle_photo_url?: string;
    vehicle_photo_front?: string;
    vehicle_photo_back?: string;
    vehicle_photo_side?: string;
    vehicle_registration_doc?: string;
  };
  bank_info?: {
    bank_name: string;
    account_number: string;
    account_name: string;
  };
  business?: {
    name?: string;
    address?: string;
  };
}

// Frontend user interface
export interface User {
  id: string;
  numeric_id?: number;
  email: string;
  name: string;
  first_name?: string;
  last_name?: string;
  phone: string;
  role: 'customer' | 'rider' | 'merchant' | 'admin';
  role_name?: string;
  is_approved?: boolean;
  is_verified?: boolean;
  email_verified?: boolean;
  phone_verified?: boolean;
  verification_status?: 'unverified' | 'email_verified' | 'phone_verified' | 'fully_verified';
  profile_complete?: boolean;
  approval_status?: string;
  rejection_reason?: string;
  created_at?: string;
  is_super_admin?: boolean;
  rider_photo_url?: string;
  average_rating?: number;
  total_ratings?: number;
  account_status?: 'active' | 'suspended' | 'banned';
  suspended_at?: string;
  suspension_expires_at?: string;
  suspended_by_admin_id?: number;
  suspension_reason?: string;
  reinstated_at?: string;
  reinstated_by_admin_id?: number;
  customer_type?: 'individual' | 'corporate';
  company_name?: string;
  company_registration_number?: string;
  company_address?: string;
  company_registration_doc_url?: string;
  company_logo_url?: string;
  merchant_info?: {
    merchant_code?: string;
    vehicle_types?: string[];
    earnings_split_percentage?: number;
    business_name?: string;
    dispatchers_count?: number;
  };
  location?: {
    latitude: number;
    longitude: number;
  };
  vehicle?: {
    vehicle_type: string;
    license_plate: string;
    make?: string;
    model?: string;
    year?: number;
    color?: string;
    capacity_kg: number;
    vehicle_photo_url?: string;
    vehicle_photo_front?: string;
    vehicle_photo_back?: string;
    vehicle_photo_side?: string;
    vehicle_registration_doc?: string;
  };
  bank_info?: {
    bank_name: string;
    account_number: string;
    account_name: string;
  };
  business?: {
    name?: string;
    address?: string;
  };
}

// Map backend user to frontend user
const mapBackendUserToFrontend = (backendUser: BackendUser): User => {
  return {
    id: backendUser.uuid,
    numeric_id: backendUser.id,
    email: backendUser.email,
    name: `${backendUser.first_name || ''} ${backendUser.last_name || ''}`.trim() || backendUser.email,
    first_name: backendUser.first_name || '',
    last_name: backendUser.last_name || '',
    phone: backendUser.phone,
    role: backendUser.role_name as 'customer' | 'rider' | 'merchant' | 'admin',
    role_name: backendUser.role_name,
    is_approved: backendUser.approval_status === 'approved',
    is_verified: backendUser.email_verified && backendUser.phone_verified,
    email_verified: backendUser.email_verified || false,
    phone_verified: backendUser.phone_verified || false,
    verification_status: backendUser.verification_status || 'unverified',
    profile_complete: backendUser.profile_complete ?? true,
    approval_status: backendUser.approval_status,
    rejection_reason: backendUser.rejection_reason,
    created_at: backendUser.created_at,
    is_super_admin: backendUser.is_super_admin || false,
    rider_photo_url: backendUser.rider_photo_url,
    average_rating: backendUser.average_rating,
    total_ratings: backendUser.total_ratings,
    account_status: backendUser.account_status || 'active',
    suspended_at: backendUser.suspended_at,
    suspension_expires_at: backendUser.suspension_expires_at,
    suspended_by_admin_id: backendUser.suspended_by_admin_id,
    suspension_reason: backendUser.suspension_reason,
    reinstated_at: backendUser.reinstated_at,
    reinstated_by_admin_id: backendUser.reinstated_by_admin_id,
    customer_type: backendUser.customer_type || 'individual',
    company_name: backendUser.corporate_info?.company_name,
    company_registration_number: backendUser.corporate_info?.company_registration_number,
    company_address: backendUser.corporate_info?.company_address,
    company_registration_doc_url: backendUser.corporate_info?.company_registration_doc_url,
    company_logo_url: backendUser.corporate_info?.company_logo_url,
    merchant_info: backendUser.merchant_info,
    location: (backendUser.current_latitude && backendUser.current_longitude) ? {
      latitude: backendUser.current_latitude,
      longitude: backendUser.current_longitude
    } : undefined,
    vehicle: backendUser.vehicle,
    bank_info: backendUser.bank_info,
    business: backendUser.business
  };
};

interface BackendAuthResponse {
  access_token: string;
  token_type: string;
  jti: string;
  user: BackendUser;
}

export interface AuthResponse {
  access_token: string;
  user: User;
}

export interface TwoFactorLoginResponse {
  requires_2fa: boolean;
  user_id: number;
  message: string;
}

export interface TwoFactorStatusResponse {
  two_factor_enabled: boolean;
  enabled_at?: string;
}

export type LoginResponse = AuthResponse | TwoFactorLoginResponse;

export function isTwoFactorResponse(response: LoginResponse): response is TwoFactorLoginResponse {
  return 'requires_2fa' in response && response.requires_2fa === true;
}

export interface Order {
  id: string;
  customer_id: string;
  status: 'pending' | 'pending_payment' | 'awaiting_payment' | 'accepted' | 'picked_up' | 'delivered' | 'cancelled';
  pickup_location: string;
  dropoff_location: string;
  stops?: string[];
  package_type: string;
  package_weight: number;
  total_price: number;
  final_price?: number;
  rider_id?: string;
  created_at: string;
  estimated_delivery_time?: string;
  payment_url?: string;
}

export interface Wallet {
  id: string;
  user_id: string;
  balance: number;
}

// Authentication API calls
export const authAPI = {
  // Login - now returns either auth response or 2FA required response
  login: async (email: string, password: string): Promise<LoginResponse> => {
    const response = await api.post('/auth/login', {
      email,
      password,
    });
    
    // Check if 2FA is required
    if (response.data.requires_2fa) {
      return response.data as TwoFactorLoginResponse;
    }
    
    // Normal login response
    return {
      access_token: response.data.access_token,
      user: mapBackendUserToFrontend(response.data.user)
    };
  },

  // Verify 2FA code to complete login
  verify2FALogin: async (userId: number, otpCode: string): Promise<AuthResponse> => {
    const response: AxiosResponse<BackendAuthResponse> = await api.post('/auth/2fa/verify', {
      user_id: userId,
      otp_code: otpCode
    });
    return {
      access_token: response.data.access_token,
      user: mapBackendUserToFrontend(response.data.user)
    };
  },

  // Get 2FA status
  get2FAStatus: async (): Promise<TwoFactorStatusResponse> => {
    const response = await api.get('/auth/2fa/status');
    return response.data;
  },

  // Enable 2FA
  enable2FA: async (password: string): Promise<TwoFactorStatusResponse> => {
    const response = await api.post('/auth/2fa/enable', { password });
    return response.data;
  },

  // Disable 2FA
  disable2FA: async (password: string, otpCode: string): Promise<TwoFactorStatusResponse> => {
    const response = await api.post('/auth/2fa/disable', { password, otp_code: otpCode });
    return response.data;
  },

  // Send 2FA code (for disabling)
  send2FACode: async (): Promise<{ message: string }> => {
    const response = await api.post('/auth/2fa/send-code');
    return response.data;
  },

  // Register Customer
  registerCustomer: async (userData: {
    email: string;
    password: string;
    name: string;
    phone: string;
  }): Promise<AuthResponse> => {
    // Split name into first_name and last_name for backend
    const nameParts = userData.name.split(' ');
    const backendData = {
      email: userData.email,
      password: userData.password,
      first_name: nameParts[0] || '',
      last_name: nameParts.slice(1).join(' ') || '',
      phone: userData.phone
    };
    const response: AxiosResponse<BackendAuthResponse> = await api.post('/register/customer', backendData);
    return {
      access_token: response.data.access_token,
      user: mapBackendUserToFrontend(response.data.user)
    };
  },

  // Register Rider
  registerRider: async (userData: {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    phone: string;
    vehicle_type: string;
    license_plate: string;
    capacity_kg: number;
    make: string;
    model?: string;
    year?: number;
    color: string;
    bank_name: string;
    account_number: string;
    account_name: string;
    merchant_code?: string;
  }): Promise<BackendAuthResponse> => {
    const response: AxiosResponse<BackendAuthResponse> = await api.post('/register/rider', userData);
    return response.data;
  },

  // Register Merchant (Super Dispatcher)
  registerMerchant: async (userData: {
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    phone: string;
    business_name: string;
    business_address: string;
    company_registration_number: string;
    vehicle_types: string[];
    bank_name: string;
    account_number: string;
    account_name: string;
  }): Promise<BackendAuthResponse> => {
    const response: AxiosResponse<BackendAuthResponse> = await api.post('/register/merchant', userData);
    return response.data;
  },

  // Validate merchant code
  validateMerchantCode: async (merchantCode: string): Promise<{
    valid: boolean;
    merchant_name: string | null;
    message: string;
  }> => {
    const response = await api.get(`/merchant/validate-code/${merchantCode}`);
    return response.data;
  },

  // Get current user profile
  getProfile: async (): Promise<User> => {
    const response: AxiosResponse<BackendUser> = await api.get('/user/profile');
    return mapBackendUserToFrontend(response.data);
  },

  // Logout
  logout: async (): Promise<void> => {
    await api.post('/auth/logout');
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
  },

  // Update rider location (for dispatchers/riders only)
  updateRiderLocation: async (locationData: {
    latitude: number;
    longitude: number;
    is_available: boolean;
  }): Promise<{ message: string; location_updated_at: string }> => {
    const response = await api.put('/rider/location', locationData);
    return response.data;
  },
};

// Backend Order interfaces
interface BackendStop {
  address: string;
  lat: number;
  lng: number;
  type: 'pickup' | 'dropoff';
  contact_name: string;
  contact_phone: string;
  instructions?: string;
}

interface BackendOrder {
  id: number;
  uuid: string;
  customer_id: number;
  rider_id?: number;
  status: string;
  package_description?: string;
  package_type?: string;
  package_weight_kg?: number;
  dimensions_cm?: string;
  fragile?: boolean;
  vehicle_type?: string;
  special_instructions?: string;
  required_pickup_time?: string;
  payment_method?: string;
  promo_code?: string;
  discount_amount?: number;
  stops: BackendStop[];
  total_distance_km?: number;
  total_duration_minutes?: number;
  base_price?: number;
  final_price?: number;
  created_at: string;
  assigned_at?: string;
  started_at?: string;
  completed_at?: string;
}

// Orders API calls
export const ordersAPI = {
  // Create new order
  createOrder: async (orderData: {
    package_description?: string;
    package_type?: string;
    package_weight_kg?: number;
    dimensions_cm?: string;
    fragile?: boolean;
    vehicle_type: string;
    special_instructions?: string;
    required_pickup_time?: string;
    payment_method?: string;
    promo_code?: string;
    stops: BackendStop[];
  }): Promise<BackendOrder> => {
    const response: AxiosResponse<BackendOrder> = await api.post('/order/create', orderData);
    return response.data;
  },

  // Get user's orders
  getUserOrders: async (): Promise<any> => {
    const response = await api.get('/user/orders');
    return response.data.orders || [];
  },

  // Get available jobs for dispatchers (all pending unassigned orders)
  getAvailableJobs: async (): Promise<any> => {
    const response = await api.get('/api/rider/available-jobs');
    return response.data.orders || [];
  },

  // Get order by ID
  getOrder: async (orderId: string): Promise<BackendOrder> => {
    const response: AxiosResponse<BackendOrder> = await api.get(`/order/${orderId}`);
    return response.data;
  },

  // Update order status (for riders)
  updateOrderStatus: async (orderId: string, status: string): Promise<{ message: string }> => {
    const response = await api.put(`/order/${orderId}/status?status=${status}`);
    return response.data;
  },

  // Allocate rider to order
  allocateRider: async (orderId: string): Promise<any> => {
    const response = await api.post('/rider/allocate', { order_id: parseInt(orderId) });
    return response.data;
  },

  // Accept order (for riders)
  acceptOrder: async (orderId: string): Promise<any> => {
    const response = await api.put(`/order/${orderId}/status?status=assigned`);
    return response.data;
  },

  // Get OTP for order (for customers)
  getOrderOTP: async (orderId: string): Promise<{ otp_code: string; expiry_time: string; status: string }> => {
    const response = await api.get(`/order/${orderId}/otp`);
    return response.data;
  },

  // Validate OTP for delivery (for riders)
  validateOTP: async (orderId: number, otpCode: string): Promise<{ success: boolean; message: string; order_status?: string }> => {
    const response = await api.post('/order/validate-otp', {
      order_id: orderId,
      otp_code: otpCode
    });
    return response.data;
  },

  // Upload pickup photo (for riders)
  uploadPickupPhoto: async (orderId: string, photoFile: File): Promise<{ message: string; photo_url: string }> => {
    const formData = new FormData();
    formData.append('pickup_photo', photoFile);
    
    const response = await api.post(`/order/${orderId}/upload-pickup-photo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Upload delivery photo (for riders)
  uploadDeliveryPhoto: async (orderId: string, photoFile: File): Promise<{ message: string; photo_url: string }> => {
    const formData = new FormData();
    formData.append('delivery_photo', photoFile);
    
    const response = await api.post(`/order/${orderId}/upload-delivery-photo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Resume payment for abandoned order
  resumePayment: async (orderId: number): Promise<{ status: string; payment_url: string; reference: string; order_id: number }> => {
    const response = await api.post(`/api/order/${orderId}/resume-payment`);
    return response.data;
  },

  // Initialize payment for order (card payment)
  initializePayment: async (orderData: {
    package_description?: string;
    package_type?: string;
    package_weight_kg?: number;
    dimensions_cm?: string;
    fragile?: boolean;
    vehicle_type: string;
    special_instructions?: string;
    required_pickup_time?: string;
    payment_method: string;
    promo_code?: string;
    stops: BackendStop[];
  }): Promise<{
    status: string;
    order_id: number;
    authorization_url: string;
    reference: string;
    amount: number;
  }> => {
    const response = await api.post('/api/order/initialize-payment', orderData);
    return response.data;
  },

  // Verify payment for order (Paystack)
  verifyPayment: async (reference: string): Promise<{
    status: string;
    message: string;
    order_id?: number;
    verified: boolean;
    gateway?: string;
  }> => {
    const response = await api.post(`/api/order/verify-payment/${reference}`);
    return response.data;
  },

  // Initialize SeerBit payment for order
  initializePaymentSeerBit: async (orderData: {
    package_description?: string;
    package_type?: string;
    package_weight_kg?: number;
    dimensions_cm?: string;
    fragile?: boolean;
    vehicle_type: string;
    special_instructions?: string;
    required_pickup_time?: string;
    payment_method: string;
    promo_code?: string;
    stops: BackendStop[];
  }): Promise<{
    status: string;
    order_id: number;
    authorization_url: string;
    reference: string;
    amount: number;
    gateway: string;
  }> => {
    const response = await api.post('/api/seerbit/initialize-payment', orderData);
    return response.data;
  },

  // Verify SeerBit payment for order
  verifyPaymentSeerBit: async (reference: string): Promise<{
    status: string;
    message: string;
    order_id?: number;
    verified: boolean;
    gateway?: string;
  }> => {
    const response = await api.post(`/api/seerbit/verify-payment/${reference}`);
    return response.data;
  },

  // Get real-time tracking information for order
  getOrderTracking: async (orderId: number): Promise<{
    order_id: number;
    order_status: string;
    rider_location?: { lat: number; lng: number };
    rider_location_age_seconds?: number;
    pickup_location: { lat: number; lng: number; address: string };
    dropoff_location: { lat: number; lng: number; address: string };
    rider_to_pickup_eta?: {
      distance_km: number;
      duration_minutes: number;
      duration_in_traffic_minutes: number;
      eta_timestamp: string;
    };
    rider_to_dropoff_eta?: {
      distance_km: number;
      duration_minutes: number;
      duration_in_traffic_minutes: number;
      eta_timestamp: string;
    };
    total_distance_km?: number;
    rider_info?: { name: string; phone: string; vehicle_type: string };
    route_polyline?: string;
    last_updated: string;
  }> => {
    const response = await api.get(`/order/${orderId}/tracking`);
    return response.data;
  },
};

// Wallet API calls
export const walletAPI = {
  // Get wallet balance and transactions
  getWallet: async (): Promise<any> => {
    const response = await api.get('/user/wallet');
    return response.data;
  },

  // Add funds to wallet (placeholder - needs implementation)
  addFunds: async (amount: number): Promise<{ message: string }> => {
    // This would typically integrate with a payment provider
    throw new Error('Add funds functionality needs payment provider integration');
  },

  // Create withdrawal request
  createWithdrawal: async (data: {
    amount: number;
    bank_name: string;
    account_number: string;
    account_name: string;
  }): Promise<{
    id: number;
    amount: number;
    status: string;
    reference: string;
    message: string;
  }> => {
    const response = await api.post('/api/wallet/withdrawals', data);
    return response.data;
  },

  // Get withdrawal history
  getWithdrawals: async (): Promise<Array<{
    id: number;
    amount: number;
    status: string;
    bank_name: string;
    account_number: string;
    account_name: string;
    reference: string;
    created_at: string;
    processed_at?: string;
    processed_by?: string;
    rejection_reason?: string;
  }>> => {
    const response = await api.get('/api/wallet/withdrawals');
    return response.data.withdrawals;
  },
};

// Pricing API calls
export const pricingAPI = {
  // Get available vehicle types (public endpoint)
  getVehicleTypes: async (): Promise<{ pricing_configs: any[] }> => {
    const response = await api.get('/api/pricing/vehicle-types');
    return response.data;
  },

  // Calculate bicycle pricing
  calculateBicyclePricing: async (data: {
    pickup_address: string;
    destination_address: string;
    distance_km?: number;
    duration_min?: number;
  }): Promise<any> => {
    const response = await api.post('/api/pricing/bicycle', {
      ...data,
      request_id: `price-${Date.now()}`
    });
    return response.data;
  },

  // Calculate motorcycle pricing
  calculateMotorcyclePricing: async (data: {
    pickup_address: string;
    destination_address: string;
    distance_km?: number;
    duration_min?: number;
  }): Promise<any> => {
    const response = await api.post('/api/pricing/motorcycle', {
      ...data,
      request_id: `price-${Date.now()}`
    });
    return response.data;
  },

  // Calculate route-based pricing for multi-stop orders
  calculateRoutePricing: async (data: {
    stops: Array<{
      address: string;
      lat: number;
      lng: number;
      type: 'pickup' | 'dropoff';
    }>;
    vehicle_type: string;
    package_weight_kg: number;
  }): Promise<{
    base_fare: number;
    distance_fee: number;
    duration_fee: number;
    weight_fee: number;
    total_distance_km: number;
    total_duration_minutes: number;
    subtotal: number;
    vat_amount: number;
    management_fee: number;
    paystack_fee: number;
    total: number;
    segments: Array<{
      from_address: string;
      to_address: string;
      distance_km: number;
      duration_minutes: number;
      segment_cost: number;
    }>;
    currency: string;
  }> => {
    const response = await api.post('/api/pricing/route', data);
    return response.data;
  },
};

// Admin API calls
export const adminAPI = {
  // Get admin dashboard statistics
  getStats: async (): Promise<{ total_users: number; active_orders: number; total_revenue: number; active_dispatchers: number }> => {
    const response = await api.get('/admin/stats');
    return response.data;
  },

  // Get pending users for approval
  getPendingUsers: async (): Promise<User[]> => {
    const response = await api.get('/admin/pending-approvals');
    const backendUsers = response.data.pending_users || [];
    // Map backend users to frontend User format with all details
    return backendUsers.map((backendUser: BackendUser) => mapBackendUserToFrontend(backendUser));
  },

  // Get pending corporate approvals
  getPendingCorporateApprovals: async (): Promise<User[]> => {
    const response = await api.get('/admin/pending-corporate-approvals');
    const corporateUsers = response.data.pending_corporate || [];
    // Map corporate users to frontend User format
    return corporateUsers.map((user: any) => ({
      id: user.uuid || String(user.id),
      numeric_id: user.id,
      email: user.email,
      name: `${user.first_name} ${user.last_name}`,
      first_name: user.first_name,
      last_name: user.last_name,
      phone: user.phone,
      role: 'customer' as const,
      approval_status: user.approval_status,
      rejection_reason: user.rejection_reason,
      customer_type: 'corporate' as const,
      company_name: user.corporate_info?.company_name || '',
      company_registration_number: user.corporate_info?.company_registration_number || '',
      company_address: user.corporate_info?.company_address || '',
      company_registration_doc_url: user.corporate_info?.company_registration_doc_url,
      company_logo_url: user.corporate_info?.company_logo_url,
      created_at: user.created_at,
    }));
  },

  // Approve user
  approveUser: async (userId: string): Promise<{ message: string }> => {
    const response: AxiosResponse<{ message: string }> = await api.post(`/admin/approve-user/${userId}`);
    return response.data;
  },

  // Reject user
  rejectUser: async (userId: string, reason: string): Promise<{ message: string }> => {
    const response: AxiosResponse<{ message: string }> = await api.post(`/admin/reject-user/${userId}`, { reason });
    return response.data;
  },

  // Get all users for user management
  getAllUsers: async (): Promise<User[]> => {
    const response = await api.get('/admin/users');
    const backendUsers = response.data.users || [];
    return backendUsers.map((backendUser: BackendUser) => mapBackendUserToFrontend(backendUser));
  },

  // Get all pricing configurations
  getPricingConfigs: async (): Promise<{ pricing_configs: any[] }> => {
    const response = await api.get('/admin/pricing-configs');
    return response.data;
  },

  // Update pricing configuration for a vehicle type
  updatePricingConfig: async (vehicleType: string, data: {
    icon?: string;
    base_fare_ngn: number;
    km_charge_ngn: number;
    min_charge_ngn: number;
    management_fee_rate: number;
  }): Promise<any> => {
    const response = await api.put(`/admin/pricing-configs/${vehicleType}`, data);
    return response.data;
  },

  // Create new pricing configuration for a vehicle type
  createPricingConfig: async (data: {
    vehicle_type: string;
    icon?: string;
    base_fare_ngn: number;
    km_charge_ngn: number;
    minute_charge_ngn: number;
    min_charge_ngn: number;
  }): Promise<any> => {
    const payload = {
      vehicle_type: data.vehicle_type,
      vehicle_icon: data.icon || '🚗',
      flat_fee: data.base_fare_ngn,
      rate_per_km: data.km_charge_ngn,
      rate_per_minute: data.minute_charge_ngn,
      minimum_fare: data.min_charge_ngn,
    };
    const response = await api.post('/api/admin/pricing/vehicles', payload);
    return response.data;
  },

  // Delete pricing configuration for a vehicle type
  deletePricingConfig: async (vehicleType: string): Promise<any> => {
    const response = await api.delete(`/admin/pricing-configs/${vehicleType}`);
    return response.data;
  },

  // === AI Pricing Management APIs ===
  
  // Vehicle Pricing (new AI-based)
  getVehiclePricing: async (): Promise<any[]> => {
    const response = await api.get('/api/admin/pricing/vehicles');
    return response.data;
  },
  
  createVehiclePricing: async (data: any): Promise<any> => {
    const response = await api.post('/api/admin/pricing/vehicles', data);
    return response.data;
  },
  
  updateVehiclePricing: async (id: number, data: any): Promise<any> => {
    const response = await api.put(`/api/admin/pricing/vehicles/${id}`, data);
    return response.data;
  },
  
  deleteVehiclePricing: async (id: number): Promise<any> => {
    const response = await api.delete(`/api/admin/pricing/vehicles/${id}`);
    return response.data;
  },

  // Platform Fees
  getPlatformFees: async (): Promise<any[]> => {
    const response = await api.get('/api/admin/pricing/fees');
    return response.data;
  },
  
  createPlatformFee: async (data: any): Promise<any> => {
    const response = await api.post('/api/admin/pricing/fees', data);
    return response.data;
  },
  
  updatePlatformFee: async (id: number, data: any): Promise<any> => {
    const response = await api.put(`/api/admin/pricing/fees/${id}`, data);
    return response.data;
  },
  
  deletePlatformFee: async (id: number): Promise<any> => {
    const response = await api.delete(`/api/admin/pricing/fees/${id}`);
    return response.data;
  },

  // AI Pricing Config
  getAIPricingConfig: async (): Promise<any> => {
    const response = await api.get('/api/admin/pricing/ai-config');
    return response.data;
  },
  
  updateAIPricingConfig: async (data: any): Promise<any> => {
    const response = await api.put('/api/admin/pricing/ai-config', data);
    return response.data;
  },

  // Pricing Zones
  getPricingZones: async (): Promise<any[]> => {
    const response = await api.get('/api/admin/pricing/zones');
    return response.data;
  },
  
  createPricingZone: async (data: any): Promise<any> => {
    const response = await api.post('/api/admin/pricing/zones', data);
    return response.data;
  },
  
  updatePricingZone: async (id: number, data: any): Promise<any> => {
    const response = await api.put(`/api/admin/pricing/zones/${id}`, data);
    return response.data;
  },
  
  deletePricingZone: async (id: number): Promise<any> => {
    const response = await api.delete(`/api/admin/pricing/zones/${id}`);
    return response.data;
  },

  // Time Pricing Rules
  getTimePricingRules: async (): Promise<any[]> => {
    const response = await api.get('/api/admin/pricing/time-rules');
    return response.data;
  },
  
  createTimePricingRule: async (data: any): Promise<any> => {
    const response = await api.post('/api/admin/pricing/time-rules', data);
    return response.data;
  },
  
  updateTimePricingRule: async (id: number, data: any): Promise<any> => {
    const response = await api.put(`/api/admin/pricing/time-rules/${id}`, data);
    return response.data;
  },
  
  deleteTimePricingRule: async (id: number): Promise<any> => {
    const response = await api.delete(`/api/admin/pricing/time-rules/${id}`);
    return response.data;
  },

  // Get all orders with full details for order management
  getAllOrders: async (): Promise<{ orders: any[] }> => {
    const response = await api.get('/admin/orders');
    return response.data;
  },

  // Approve a delivery
  approveDelivery: async (orderId: number, notes?: string): Promise<{ message: string; order_id: number; approval_status: string }> => {
    const response = await api.post(`/admin/orders/${orderId}/approve`, null, {
      params: { notes }
    });
    return response.data;
  },

  // Send message to dispatcher about a delivery
  sendDispatcherMessage: async (orderId: number, message: string): Promise<{ message: string; order_id: number; dispatcher_id: number }> => {
    const response = await api.post(`/admin/orders/${orderId}/message`, null, {
      params: { message }
    });
    return response.data;
  },

  // Get specific user details
  getUserDetails: async (userId: number): Promise<any> => {
    const response = await api.get(`/admin/user/${userId}`);
    return response.data;
  },

  // Update user
  updateUser: async (userId: number, updates: any): Promise<any> => {
    const response = await api.put(`/admin/user/${userId}`, updates);
    return response.data;
  },

  // Delete user
  deleteUser: async (userId: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/admin/user/${userId}`);
    return response.data;
  },

  // Get user dependencies (for delete impact preview)
  getUserDependencies: async (userId: number): Promise<{
    user_id: number;
    user_email: string;
    user_name: string;
    order_count: number;
    vehicle_count: number;
    wallet_balance: number;
    can_delete: boolean;
    blocking_reasons: string[];
  }> => {
    const response = await api.get(`/admin/user/${userId}/dependencies`);
    return response.data;
  },

  // Get specific order details
  getOrderDetails: async (orderId: number): Promise<any> => {
    const response = await api.get(`/admin/order/${orderId}`);
    return response.data;
  },

  // Update order
  updateOrder: async (orderId: number, updates: any): Promise<any> => {
    const response = await api.put(`/admin/order/${orderId}`, updates);
    return response.data;
  },

  // Delete order
  deleteOrder: async (orderId: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/admin/order/${orderId}`);
    return response.data;
  },

  // Create and send announcement to all users
  createAnnouncement: async (data: { title: string; message: string }): Promise<{
    id: number;
    admin_id: number;
    admin_name: string;
    title: string;
    message: string;
    is_active: boolean;
    created_at: string;
  }> => {
    const response = await api.post('/api/admin/announcements', data);
    return response.data;
  },

  // Get all announcements (admin view)
  getAnnouncements: async (): Promise<{
    announcements: Array<{
      id: number;
      admin_id: number;
      admin_name: string;
      title: string;
      message: string;
      is_active: boolean;
      created_at: string;
    }>;
    total: number;
  }> => {
    const response = await api.get('/api/admin/announcements');
    return response.data;
  },

  // Get all support tickets (admin view)
  getSupportTickets: async (status?: string): Promise<{
    tickets: Array<{
      id: number;
      ticket_number: string;
      user_id: number;
      user_name: string;
      user_email: string;
      subject: string;
      message: string;
      status: string;
      priority: string;
      category?: string;
      admin_id?: number;
      admin_name?: string;
      admin_response?: string;
      responded_at?: string;
      resolved_at?: string;
      created_at: string;
      updated_at: string;
    }>;
    total: number;
  }> => {
    const params = status ? { status } : {};
    const response = await api.get('/api/admin/support/tickets', { params });
    return response.data;
  },

  // Respond to a support ticket (admin)
  respondToSupportTicket: async (ticketId: number, data: {
    admin_response: string;
    status?: string;
  }): Promise<{
    id: number;
    ticket_number: string;
    user_id: number;
    user_name: string;
    user_email: string;
    subject: string;
    message: string;
    status: string;
    priority: string;
    category?: string;
    admin_id?: number;
    admin_name?: string;
    admin_response?: string;
    responded_at?: string;
    resolved_at?: string;
    created_at: string;
    updated_at: string;
  }> => {
    const response = await api.post(`/api/admin/support/tickets/${ticketId}/respond`, data);
    return response.data;
  },

  // Update support ticket status (admin)
  updateSupportTicketStatus: async (ticketId: number, status: string): Promise<{
    id: number;
    ticket_number: string;
    status: string;
  }> => {
    const response = await api.patch(`/api/admin/support/tickets/${ticketId}/status`, { status });
    return response.data;
  },

  // Get all withdrawal requests (admin)
  getWithdrawalRequests: async (status?: string): Promise<{
    withdrawals: Array<{
      id: number;
      reference: string;
      amount: number;
      status: string;
      bank_name: string;
      account_number: string;
      account_name: string;
      admin_note?: string;
      created_at: string;
      processed_at?: string;
      user: {
        id: number;
        uuid: string;
        email: string;
        first_name: string;
        last_name: string;
        phone: string;
      };
      wallet_balance: number;
    }>;
    total: number;
  }> => {
    const response = await api.get('/api/admin/withdrawals', {
      params: { status }
    });
    return response.data;
  },

  // Approve withdrawal request (admin)
  approveWithdrawal: async (withdrawalId: number): Promise<{
    success: boolean;
    message: string;
    withdrawal: {
      id: number;
      reference: string;
      amount: number;
      status: string;
      processed_at: string;
    };
  }> => {
    const response = await api.post(`/api/admin/withdrawals/${withdrawalId}/approve`);
    return response.data;
  },

  // Reject withdrawal request (admin)
  rejectWithdrawal: async (withdrawalId: number, adminNote: string): Promise<{
    success: boolean;
    message: string;
    withdrawal: {
      id: number;
      reference: string;
      amount: number;
      status: string;
      admin_note: string;
      processed_at: string;
    };
  }> => {
    const response = await api.post(`/api/admin/withdrawals/${withdrawalId}/reject`, {
      admin_note: adminNote
    });
    return response.data;
  },

  // Suspend user account
  suspendUser: async (userId: number, data: {
    suspension_type: 'temporary' | 'permanent';
    reason: string;
    duration_hours?: number;
    notify_user?: boolean;
  }): Promise<{
    success: boolean;
    message: string;
    suspension_info: any;
  }> => {
    const response = await api.post(`/admin/users/${userId}/suspend`, data);
    return response.data;
  },

  // Unsuspend user account
  unsuspendUser: async (userId: number, data: {
    note?: string;
    notify_user?: boolean;
  }): Promise<{
    success: boolean;
    message: string;
    suspension_info: any;
  }> => {
    const response = await api.post(`/admin/users/${userId}/unsuspend`, data);
    return response.data;
  },
};

// Rider API calls
export const riderAPI = {
  // Complete dispatcher profile (for bulk-imported dispatchers)
  completeProfile: async (profileData: {
    vehicle_type: string;
    license_plate: string;
    capacity_kg: number;
    make: string;
    model?: string | null;
    year?: number | null;
    color: string;
    bank_name: string;
    account_number: string;
    account_name: string;
    rider_photo_url?: string;
    vehicle_photo_front?: string;
    vehicle_photo_back?: string | null;
    vehicle_photo_side?: string | null;
    vehicle_registration_doc?: string | null;
  }): Promise<{ message: string }> => {
    const response = await api.post('/auth/dispatcher/complete-profile', profileData);
    return response.data;
  },

  // Update rider location
  updateLocation: async (latitude: number, longitude: number, is_available: boolean): Promise<{ message: string }> => {
    const response = await api.put('/rider/location', {
      latitude,
      longitude,
      is_available
    });
    return response.data;
  },

  // Get rider notifications
  getNotifications: async (limit?: number): Promise<{
    notifications: Array<{
      id: string;
      type: string;
      title: string;
      message: string;
      time: string;
      read: boolean;
    }>;
    total: number;
    unread: number;
  }> => {
    const response = await api.get('/api/rider/notifications', {
      params: { limit }
    });
    return response.data;
  },
};

// Places API calls (Google Maps)
export const placesAPI = {
  // Get address autocomplete predictions with optional country filter
  autocomplete: async (input: string, country?: string): Promise<any> => {
    const response = await api.get('/api/places/autocomplete', {
      params: { input, country }
    });
    return response.data;
  },

  // Get place details (coordinates) from place_id
  details: async (placeId: string): Promise<{ formatted_address: string; lat: number; lng: number }> => {
    const response = await api.get('/api/places/details', {
      params: { place_id: placeId }
    });
    return response.data;
  },

  // Reverse geocode - convert coordinates to address
  reverseGeocode: async (lat: number, lng: number): Promise<{ 
    formatted_address: string; 
    lat: number; 
    lng: number;
    place_id: string;
  }> => {
    const response = await api.get('/api/places/reverse-geocode', {
      params: { lat, lng }
    });
    return response.data;
  },
};

// Communication Hub API
export interface ConversationMessage {
  id: number;
  conversation_id: number;
  sender_id: number;
  sender_type: 'admin' | 'dispatcher' | 'customer' | 'merchant' | 'participant';
  sender_name?: string;
  content: string;
  message_type: string;
  attachment_url?: string;
  attachment_name?: string;
  attachment_type?: string;
  is_read: boolean;
  read_at?: string;
  is_edited: boolean;
  is_deleted: boolean;
  created_at: string;
}

export interface Conversation {
  id: number;
  admin_id: number;
  admin_name?: string;
  participant_id: number;
  participant_type: 'dispatcher' | 'customer' | 'merchant';
  participant_name?: string;
  participant_phone?: string;
  participant_email?: string;
  participant_photo?: string;
  dispatcher_id?: number;
  dispatcher_name?: string;
  dispatcher_phone?: string;
  dispatcher_photo?: string;
  subject?: string;
  status: string;
  priority: string;
  admin_unread_count: number;
  participant_unread_count: number;
  dispatcher_unread_count?: number;
  last_message_preview?: string;
  last_message_at?: string;
  last_message_by?: string;
  created_at: string;
}

export interface ConversationWithMessages extends Conversation {
  messages: ConversationMessage[];
}

export interface CommunicationStats {
  total_conversations: number;
  active_conversations: number;
  unread_messages: number;
  total_announcements: number;
  pending_announcements: number;
  total_recipients_reached: number;
}

export interface AnnouncementData {
  id: number;
  admin_id: number;
  admin_name?: string;
  title: string;
  message: string;
  announcement_type: string;
  priority: string;
  target_audience: string;
  target_filter?: Record<string, any>;
  total_recipients: number;
  delivered_count: number;
  read_count: number;
  status: string;
  is_active: boolean;
  send_push: boolean;
  send_email: boolean;
  send_sms: boolean;
  scheduled_at?: string;
  sent_at?: string;
  expires_at?: string;
  created_at: string;
}

export const communicationAPI = {
  getStats: async (): Promise<CommunicationStats> => {
    const response = await api.get('/admin/communication/stats');
    return response.data;
  },

  getConversations: async (params?: {
    status?: string;
    participant_type?: string;
    search?: string;
    skip?: number;
    limit?: number;
  }): Promise<Conversation[]> => {
    const response = await api.get('/admin/conversations', { params });
    return response.data;
  },

  getConversation: async (conversationId: number): Promise<ConversationWithMessages> => {
    const response = await api.get(`/admin/conversations/${conversationId}`);
    return response.data;
  },

  getUsersByType: async (userType: string, params?: {
    search?: string;
    skip?: number;
    limit?: number;
  }): Promise<Array<{
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    photo: string | null;
    user_type: string;
  }>> => {
    const response = await api.get(`/admin/users/by-type/${userType}`, { params });
    return response.data;
  },

  createConversation: async (data: {
    participant_id: number;
    participant_type?: string;
    dispatcher_id?: number;
    subject?: string;
    initial_message: string;
  }): Promise<ConversationWithMessages> => {
    const response = await api.post('/admin/conversations', data);
    return response.data;
  },

  sendMessage: async (conversationId: number, data: {
    content: string;
    message_type?: string;
    attachment_url?: string;
    attachment_name?: string;
    attachment_type?: string;
  }): Promise<ConversationMessage> => {
    const response = await api.post(`/admin/conversations/${conversationId}/messages`, data);
    return response.data;
  },

  sendAnnouncement: async (data: {
    title: string;
    message: string;
    announcement_type?: string;
    priority?: string;
    target_audience: string;
    target_filter?: Record<string, any>;
    target_user_ids?: number[];
    send_push?: boolean;
    send_email?: boolean;
    send_sms?: boolean;
    expires_at?: string;
  }): Promise<AnnouncementData> => {
    const response = await api.post('/admin/announcements/send', data);
    return response.data;
  },

  getAnnouncementHistory: async (params?: {
    skip?: number;
    limit?: number;
    announcement_type?: string;
  }): Promise<{ announcements: AnnouncementData[]; total: number }> => {
    const response = await api.get('/admin/announcements/history', { params });
    return response.data;
  },
};

export const dispatcherCommunicationAPI = {
  getConversations: async (): Promise<Conversation[]> => {
    const response = await api.get('/dispatcher/conversations');
    return response.data;
  },

  getConversation: async (conversationId: number): Promise<ConversationWithMessages> => {
    const response = await api.get(`/dispatcher/conversations/${conversationId}`);
    return response.data;
  },

  sendMessage: async (conversationId: number, data: {
    content: string;
    message_type?: string;
  }): Promise<ConversationMessage> => {
    const response = await api.post(`/dispatcher/conversations/${conversationId}/messages`, data);
    return response.data;
  },

  getAnnouncements: async (params?: {
    skip?: number;
    limit?: number;
  }): Promise<{ announcements: AnnouncementData[]; total: number }> => {
    const response = await api.get('/dispatcher/announcements', { params });
    return response.data;
  },

  markAnnouncementRead: async (announcementId: number): Promise<{ success: boolean }> => {
    const response = await api.post(`/dispatcher/announcements/${announcementId}/read`);
    return response.data;
  },
};

// Merchant API endpoints
export const merchantAPI = {
  getStats: async (): Promise<{
    total_dispatchers: number;
    active_dispatchers: number;
    total_completed_jobs: number;
    total_earnings: number;
    pending_earnings: number;
    earnings_split_percentage: number;
    available_jobs_count: number;
  }> => {
    const response = await api.get('/merchant/stats');
    return response.data;
  },

  getAvailableJobs: async (): Promise<{
    jobs: Array<{
      order_id: number;
      order_uuid: string;
      customer_name: string;
      customer_type: string;
      package_description: string;
      package_type: string;
      vehicle_type: string;
      stops: any[];
      total_distance_km: number;
      final_price: number;
      created_at: string;
    }>;
    total_count: number;
    vehicle_types_filter: string[];
  }> => {
    const response = await api.get('/merchant/available-jobs');
    return response.data;
  },

  acceptJob: async (orderId: number): Promise<{
    success: boolean;
    message: string;
    order: {
      id: number;
      uuid: string;
      status: string;
      accepted_at: string;
    };
  }> => {
    const response = await api.post(`/merchant/accept-job/${orderId}`);
    return response.data;
  },

  getMyJobs: async (status?: string): Promise<{
    jobs: Array<{
      order_id: number;
      order_uuid: string;
      status: string;
      customer_name: string;
      package_description: string;
      vehicle_type: string;
      stops: any[];
      total_distance_km: number;
      final_price: number;
      created_at: string;
      accepted_at: string | null;
      completed_at: string | null;
    }>;
    total_count: number;
  }> => {
    const response = await api.get('/merchant/my-jobs', { params: { status } });
    return response.data;
  },

  getMyDispatchers: async (): Promise<{
    dispatchers: Array<{
      id: number;
      uuid: string;
      email: string;
      phone: string;
      first_name: string;
      last_name: string;
      is_available: boolean;
      current_latitude: number | null;
      current_longitude: number | null;
      location_updated_at: string | null;
      approval_status: string;
      average_rating: number;
      total_ratings: number;
      vehicle: {
        vehicle_type: string;
        license_plate: string;
        make: string;
        color: string;
      } | null;
      created_at: string;
      assignment_type: 'self_registered' | 'admin_assigned';
    }>;
    total_count: number;
    by_code_count: number;
    by_admin_count: number;
  }> => {
    const response = await api.get('/merchant/my-dispatchers');
    return response.data;
  },

  updateEarningsSplit: async (percentage: number): Promise<{
    success: boolean;
    message: string;
    earnings_split_percentage: number;
  }> => {
    const response = await api.put('/merchant/earnings-split', {
      earnings_split_percentage: percentage
    });
    return response.data;
  },

  getDashboard: async (): Promise<{
    merchant_info: {
      business_name: string;
      email: string;
      phone: string;
    };
    order_stats: {
      total_orders: number;
      active_orders: number;
      completed_orders: number;
      total_revenue: number;
      pending_revenue: number;
    };
    dispatcher_stats: {
      total_dispatchers: number;
      active_now: number;
    };
    inventory_stats: {
      total_products: number;
      low_stock_items: number;
      total_inventory_value: number;
    };
    invoice_stats: {
      unpaid_invoices: number;
      total_invoice_amount: number;
    };
  }> => {
    const response = await api.get('/merchant/dashboard');
    return response.data;
  },
};

export default api;